'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { ArrowUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface Feature {
  icon: string
  title: string
  subtitle?: string
  description?: string
}

export interface CategoryFeatureSliderProps {
  features: Feature[]
  className?: string
  rawText?: boolean
}

export const CategoryFeatureSlider = ({
  features,
  className,
  rawText = false,
}: CategoryFeatureSliderProps) => {
  const isMobile = useIsMobileClient()
  const slidesPerView = isMobile ? 1.2 : 4
  const t = useTranslations()
  const getText = (text: string) => (rawText ? text : t(text))

  if (!features || features.length === 0) {
    return null
  }

  return (
    <div className={cn('bg-neutral-900 px-5 lg:px-15', className)}>
      <Carousel
        opts={{
          align: 'start',
          loop: features.length > slidesPerView,
          slidesToScroll: 1,
        }}
        plugins={[
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
        className='w-full'
      >
        <CarouselContent
          className={cn(
            '-ml-4',
            features.length < 4 ? 'lg:justify-center' : ''
          )}
        >
          {features.map((feature, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'pl-4 md:basis-1/2 lg:basis-1/4',
                'relative',
                !isMobile && features.length <= 4 && index < features.length - 1
                  ? 'lg:after:absolute lg:after:right-0 lg:after:top-1/2 lg:after:h-full lg:after:w-[1px] lg:after:-translate-y-1/2 lg:after:bg-gray-600'
                  : ''
              )}
            >
              {feature?.description ? (
                // With description - show hover effect and arrow up
                <div className='group relative cursor-pointer px-6 py-8 transition-all duration-500 ease-in-out'>
                  <div className='relative h-full'>
                    {/* Default state (visible when not hovered) */}
                    <div className='flex h-full flex-col justify-between opacity-100 transition-all duration-500 ease-in-out group-hover:opacity-0'>
                      <div className='flex w-full items-center justify-center'>
                        <Image
                          src={ImageHelper.getImageUrlCdn(feature.icon)}
                          alt={getText(feature.title)}
                          width={180}
                          height={180}
                          className='h-28 w-28 2xl:h-32 2xl:w-32'
                        />
                      </div>
                      <div className='mt-20 flex w-full items-center justify-between'>
                        <div className='text-white'>
                          <h3 className='mb-1 text-xl font-medium md:text-2xl'>
                            {getText(feature.title)}
                          </h3>
                          {feature.subtitle ? (
                            <p className='text-sm text-gray-300 md:text-lg'>
                              {getText(feature.subtitle)}
                            </p>
                          ) : null}
                        </div>
                        <button className='flex h-12 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-black 2xl:h-13 2xl:w-12'>
                          <ArrowUp className='h-6 w-6 2xl:h-8 2xl:w-8' />
                        </button>
                      </div>
                    </div>

                    {/* Hover state (visible when hovered) with slide-up animation */}
                    <div className='absolute inset-0 translate-y-4 px-4 opacity-0 transition-all duration-500 ease-in-out group-hover:translate-y-0 group-hover:opacity-100'>
                      <div>
                        <h3 className='mb-2 text-2xl font-semibold text-white'>
                          {getText(feature.title)}
                        </h3>
                        {feature.subtitle ? (
                          <p className='text-sm text-gray-300 md:text-lg'>
                            {getText(feature.subtitle)}
                          </p>
                        ) : null}
                        <p className='mt-2 border-t pt-2 text-sm leading-relaxed text-gray-300 md:text-lg'>
                          {getText(feature.description)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // No description - centered layout without hover effect or arrow
                <div className='px-6 py-8'>
                  <div className='flex flex-col items-center text-center'>
                    <div className='flex w-full items-center justify-center'>
                      <Image
                        src={ImageHelper.getImageUrlCdn(feature.icon)}
                        alt={getText(feature.title)}
                        width={180}
                        height={180}
                        className='h-28 w-28 2xl:h-32 2xl:w-32'
                      />
                    </div>
                    <div className='mt-6 text-center'>
                      <h3 className='mb-1 text-xl font-medium text-white md:text-2xl 2xl:text-3xl'>
                        {getText(feature.title)}
                      </h3>
                      {feature.subtitle ? (
                        <p className='text-sm text-gray-300 md:text-lg'>
                          {getText(feature.subtitle)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Navigation arrows */}
        <CarouselNavigation />
      </Carousel>
    </div>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-left-10'
        variant='white'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-right-10'
        variant='white'
        hideIfEnd
      />
    </>
  )
}
